import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { auth } from '../firebase';
import { useSettings } from './SettingsContext';
import nativeBridge from '../services/nativeBridge';
import { getPlatform } from '../utils/platformDetection';

const BridgeContext = createContext();

export function useBridge() {
  return useContext(BridgeContext);
}

export function BridgeProvider({ children }) {
  const { settings, isInitialized: isSettingsInitialized } = useSettings();
  const [isBridgeReady, setIsBridgeReady] = useState(false);
  const location = useLocation();
  
  // Initialize from localStorage if available
  const previousAuthState = useRef((() => {
    try {
      // Try to get previous auth state from localStorage
      const AUTH_STATE_KEY = 'ummahti_auth_state';
      const storedAuthState = localStorage.getItem(AUTH_STATE_KEY);
      return storedAuthState === 'authenticated' ? {} : null; // Return empty object for auth, null for no auth
    } catch (e) {
      return null; // Default to no auth if localStorage fails
    }
  })());

  useEffect(() => {
    let unsubscribe;
    let initializationAttempts = 0;
    const maxAttempts = 5;
    const retryDelay = 2000; // 2 seconds
    const platform = getPlatform();

    const initializeBridge = async (forceRefresh = false) => {
      try {
        console.log(`[DEBUG] Attempting bridge initialization on ${platform} platform...`, { forceRefresh });
        await nativeBridge.initializeBridge(settings, forceRefresh);
        console.log('[DEBUG] Bridge initialization successful');
        setIsBridgeReady(true); // Set ready after successful init
      } catch (error) {
        console.error('[ERROR] Bridge initialization failed:', error);
        setIsBridgeReady(false);
        // Retry logic
        if (initializationAttempts < maxAttempts) {
          initializationAttempts++;
          console.log(`[DEBUG] Retrying bridge initialization in ${retryDelay/1000} seconds (attempt ${initializationAttempts}/${maxAttempts})`);
          setTimeout(() => initializeBridge(forceRefresh), retryDelay);
        } else {
          console.error('[ERROR] Failed to initialize bridge after', maxAttempts, 'attempts');
          // Despite failing, set bridge ready to allow components to proceed in degraded mode
          setIsBridgeReady(true);
        }
      }
    };

    // Initialize when settings are ready, regardless of auth state
    unsubscribe = auth.onAuthStateChanged(async (user) => {
      // Skip bridge initialization on admin routes
      if (location.pathname.startsWith('/admin')) {
        console.log('[DEBUG] Skipping bridge initialization on admin route:', location.pathname);
        setIsBridgeReady(true); // Set ready but don't initialize
        return;
      }

      if (isSettingsInitialized) {
        console.log('[DEBUG] Settings initialized, proceeding with bridge initialization', {
          hasUser: !!user,
          previousAuthState: previousAuthState.current ? 'authenticated' : 'unauthenticated'
        });
        
        // Detect auth state change
        const wasLoggedIn = previousAuthState.current !== null;
        const isLoggedIn = user !== null;
        const didLogOut = wasLoggedIn && !isLoggedIn;
        const didLogIn = !wasLoggedIn && isLoggedIn;
        
        // Save current auth state to localStorage for page reloads
        try {
          const AUTH_STATE_KEY = 'ummahti_auth_state';
          localStorage.setItem(AUTH_STATE_KEY, isLoggedIn ? 'authenticated' : 'unauthenticated');
        } catch (e) {
          console.error('[ERROR] Failed to save auth state to localStorage:', e);
        }
        
        // Update auth state reference
        previousAuthState.current = user;
        
        // If user is not logged in, create a simplified settings object without adhan settings
        if (!user) {
          console.log('[DEBUG] No authenticated user, using simplified settings for bridge');
          // Create a clean settings object without adhan references
          const guestSettings = { 
            ...settings,
            // Explicitly set adhan to null to avoid Firebase storage access attempts
            selectedAdhan: null
          };
          nativeBridge.updateSettings(guestSettings);
        } else {
          nativeBridge.updateSettings(settings);
        }
        
        // Force refresh when logging out
        await initializeBridge(didLogOut);
        
        // Log auth state change for debugging
        if (didLogOut) {
          console.log('[DEBUG] User logged out, forced prayer times refresh');
        } else if (didLogIn) {
          console.log('[DEBUG] User logged in');
        }
      } else {
        setIsBridgeReady(false);
        console.log('[DEBUG] Waiting for settings to initialize...', {
          hasUser: !!user,
          isSettingsInitialized,
          platform: platform
        });
      }
    });

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [isSettingsInitialized, settings, location.pathname]);

  return (
    <BridgeContext.Provider value={{ nativeBridge, isBridgeReady }}>
      {children}
    </BridgeContext.Provider>
  );
} 